import { cn } from '@/lib/utils'
import { logger } from '@/ui'
import { Button } from '@/ui/rapid-ui'
import { useWhatsAppEnabled } from '@/ui/utilities/hooks/use-whatsapp-enabled'
import { campaignsShowQueryKey, useCampaignUpdateWhatsAppAutomation, useWhatsAppAccountShow } from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { CheckIcon, ZapIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

type OverrideValue = 'inherit' | 'on' | 'off'

function toOverrideValue(value: boolean | null): OverrideValue {
	if (value === true) { return 'on' }
	if (value === false) { return 'off' }
	return 'inherit'
}

export function WhatsAppAutomationOverride({
	campaignId,
	organisationId,
	value,
	scoreThreshold,
}: {
	campaignId: string
	organisationId: string | null
	value: boolean | null
	scoreThreshold: number | null
}) {
	const whatsappEnabled = useWhatsAppEnabled()
	const { t } = useTranslation('common')
	const queryClient = useQueryClient()
	const [error, setError] = useState('')
	const [justSaved, setJustSaved] = useState(false)
	const [thresholdInvalid, setThresholdInvalid] = useState(false)

	const account = useWhatsAppAccountShow(organisationId ?? '', { enabled: Boolean(organisationId) })
	const orgEnabled = Boolean(account.data?.firstContactAutomationEnabled)
	const orgThreshold = account.data?.firstContactScoreThresholdResolved ?? 89

	// A campaign overrides the org when it sets either axis explicitly.
	const isOverriding = value !== null || scoreThreshold !== null
	const [editing, setEditing] = useState(isOverriding)
	// null = enable is not overridden (follow the org); the live toggle then
	// reflects the resolved org enablement. A boolean means the campaign has
	// explicitly chosen on/off.
	const [enableDraft, setEnableDraft] = useState<boolean | null>(value)
	const [draftThreshold, setDraftThreshold] = useState<string>(scoreThreshold?.toString() ?? '')

	const autoContact = enableDraft ?? orgEnabled

	// Reflect the persisted override after a save/refetch, and open the editor
	// when the campaign already carries an override. Keyed only on the persisted
	// props so an account refetch can't clobber the user's in-progress edits.
	useEffect(() => {
		setEnableDraft(value)
		setDraftThreshold(scoreThreshold?.toString() ?? '')
		if (value !== null || scoreThreshold !== null) {
			setEditing(true)
		}
	}, [value, scoreThreshold])

	const mutation = useCampaignUpdateWhatsAppAutomation({
		onSuccess: async (_data, variables) => {
			setError('')
			// Only log an override change when this save actually touched `enabled`
			// (threshold-only saves omit it), so it never falsely reads as "inherit".
			if ('enabled' in variables.data) {
				logger.track('WhatsApp: Campaign Automation Override Set', {
					category: 'whatsapp',
					data: { campaign_id: campaignId, value: toOverrideValue(variables.data.enabled ?? null) },
				})
			}
			await queryClient.invalidateQueries({ queryKey: campaignsShowQueryKey(campaignId) })
		},
		onError: (err) => {
			logger.error(err, {
				tags: { component: 'WhatsAppAutomationOverride', action: 'updateWhatsAppAutomation' },
				data: { campaignId, responseMessage: err?.response?.data?.message },
			})
			setError(err?.response?.data?.message || t('overview.whatsappAutomationSaveError'))
		},
	})

	const openOverride = () => {
		setJustSaved(false)
		setError('')
		setThresholdInvalid(false)
		setEnableDraft(value)
		setDraftThreshold(scoreThreshold?.toString() ?? '')
		setEditing(true)
	}

	const handleSave = () => {
		setError('')
		setThresholdInvalid(false)
		let score: number | null = null
		if (autoContact) {
			const trimmed = draftThreshold.trim()
			if (trimmed !== '') {
				const parsed = Number(trimmed)
				if (!Number.isInteger(parsed) || parsed < 66 || parsed > 100) {
					setThresholdInvalid(true)
					return
				}
				score = parsed
			}
		}
		// Only send `enabled` when the campaign's choice actually changed, so a
		// threshold-only edit leaves the inherited on/off untouched.
		const data: { enabled?: boolean | null; scoreThreshold: number | null } = { scoreThreshold: score }
		if (enableDraft !== value) {
			data.enabled = enableDraft
		}
		mutation.mutate({ campaign: campaignId, data }, { onSuccess: () => setJustSaved(true) })
	}

	const handleReset = () => {
		setError('')
		setJustSaved(false)
		mutation.mutate(
			{ campaign: campaignId, data: { enabled: null, scoreThreshold: null } },
			{ onSuccess: () => setEditing(false) },
		)
	}

	if (!whatsappEnabled) {
		return null
	}

	return (
		<div className="rounded-2xl border border-grey-200 bg-white p-5 shadow-sm">
			<div className="flex items-start gap-4">
				<div className="grid size-11 shrink-0 place-items-center rounded-xl bg-purple-600 text-white shadow-sm">
					<ZapIcon className="size-5" />
				</div>

				<div className="min-w-0 flex-1">
					<h3 className="font-semibold text-grey-900">{t('overview.whatsappAutomationTitle')}</h3>
					<p className="mt-0.5 text-sm text-grey-500">{t('overview.whatsappAutomationDescription')}</p>

					{!editing
						? (
							<div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-grey-100 bg-grey-50 px-4 py-3">
								<span className="inline-flex items-center gap-2 text-sm text-grey-700">
									<span
										className={cn(
											'size-2 rounded-full',
											orgEnabled ? 'bg-green-600' : 'bg-grey-300',
										)}
									/>
									{orgEnabled
										? (
											<>
												{t('overview.whatsappAutomationFollowingOrgOn')} {orgThreshold}%
											</>
										)
										: t('overview.whatsappAutomationFollowingOrgOff')}
								</span>
								<Button type="subtle" size="short" onClick={openOverride}>
									{t('overview.whatsappAutomationOverrideCta')}
								</Button>
							</div>
						)
						: (
							<div className="mt-4 space-y-4 duration-200 animate-in fade-in slide-in-from-top-1">
								<div className="space-y-4 rounded-xl border border-grey-100 bg-grey-50/60 p-4">
									<label className="flex cursor-pointer items-center justify-between gap-4">
										<span className="text-sm font-medium text-grey-800">
											{t('overview.whatsappAutomationAutoContactLabel')}
										</span>
										<span className="relative inline-flex h-6 w-11 shrink-0 items-center">
											<input
												type="checkbox"
												checked={autoContact}
												onChange={(event) => {
													setEnableDraft(event.target.checked)
													setJustSaved(false)
												}}
												disabled={mutation.isPending}
												className="peer sr-only"
											/>
											<span className="absolute inset-0 rounded-full bg-grey-300 transition-colors peer-checked:bg-purple-600 peer-focus-visible:ring-2 peer-focus-visible:ring-purple-500 peer-focus-visible:ring-offset-2" />
											<span className="absolute left-0.5 size-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5" />
										</span>
									</label>

									{autoContact && (
										<div className="border-t border-grey-200 pt-4">
											<label
												htmlFor={`wa-threshold-${campaignId}`}
												className="block text-sm font-medium text-grey-800"
											>
												{t('overview.whatsappAutomationThresholdLabel')}
											</label>
											<div className="mt-1.5 flex flex-wrap items-center gap-3">
												<div
													className={cn(
														'inline-flex items-center rounded-lg border bg-white pl-3 pr-2 transition',
														thresholdInvalid
															? 'border-red-500 focus-within:ring-2 focus-within:ring-red-500/20'
															: 'border-grey-300 focus-within:border-purple-500 focus-within:ring-2 focus-within:ring-purple-500/20',
													)}
												>
													<input
														id={`wa-threshold-${campaignId}`}
														aria-label={t('overview.whatsappAutomationThresholdLabel')}
														aria-invalid={thresholdInvalid}
														type="number"
														min={66}
														max={100}
														value={draftThreshold}
														placeholder={String(orgThreshold)}
														onChange={(event) => {
															setDraftThreshold(event.target.value)
															setJustSaved(false)
															setThresholdInvalid(false)
														}}
														disabled={mutation.isPending}
														className="w-12 bg-transparent py-2 text-sm tabular-nums text-grey-900 outline-none placeholder:text-grey-400"
													/>
													<span className="text-sm text-grey-400">%</span>
												</div>
												<span className="text-xs text-grey-500">
													{t('overview.whatsappAutomationOrgDefault')} {orgThreshold}%
												</span>
											</div>
											<p
												className={cn(
													'mt-1.5 text-xs',
													thresholdInvalid ? 'font-medium text-red-600' : 'text-grey-500',
												)}
											>
												{t('whatsappSettings.firstContactThresholdRange')}
											</p>
										</div>
									)}
								</div>

								<div className="flex flex-wrap items-center gap-3">
									<Button
										type="committal"
										size="short"
										onClick={handleSave}
										isLoading={mutation.isPending}
									>
										{t('overview.whatsappAutomationThresholdSave')}
									</Button>
									<Button
										type="invisible"
										size="short"
										onClick={handleReset}
										disabled={mutation.isPending}
									>
										{t('overview.whatsappAutomationReset')}
									</Button>
									{justSaved && !mutation.isPending && (
										<span className="inline-flex items-center gap-1 text-sm font-medium text-green-600 animate-in fade-in">
											<CheckIcon className="size-4" />
											{t('overview.whatsappAutomationSaved')}
										</span>
									)}
								</div>

								{error && <p className="text-sm text-red-600">{error}</p>}
							</div>
						)}
				</div>
			</div>
		</div>
	)
}
